/* CallNotesDialog — corporate (QR-branding) call notes for a Scan2Call ring.
   Doctors log symptoms/tests/prescriptions; support desks keep transcripts.
   "AI insight" reads the draft: symptoms → likely conditions (with
   disclaimer); transcripts → summary. Usable mid-call and from the log. */
import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, NotebookPen, Sparkles, X, Download, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import logging from "../../utils/logging";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallNotesDialog({ ringId, visitorName, open, onClose }) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [insight, setInsight] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const sendEmail = async (e) => {
    e?.preventDefault();
    if (!emailTo.trim()) return;
    setEmailBusy(true);
    try {
      const r = await axios.post(
        `${API}/doorbell/rings/${ringId}/notes/email`,
        { to_email: emailTo.trim() },
        { withCredentials: true },
      );
      toast.success(`Notes PDF emailed to ${r.data.to}`);
      setEmailOpen(false);
      setEmailTo("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the email");
    } finally {
      setEmailBusy(false);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/doorbell/rings/${ringId}/notes/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `call-notes-${ringId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not generate the PDF");
      logging.error("Notes PDF", err);
    } finally {
      setPdfBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/doorbell/rings/${ringId}/notes`, { withCredentials: true });
      setNotes(r.data.notes || []);
    } catch (err) { logging.error("Load notes", err); }
  }, [ringId]);

  useEffect(() => {
    if (open) { setDraft(""); setInsight(null); load(); }
  }, [open, load]);

  const askAi = async () => {
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/doorbell/notes/ai-insight`, { text: draft }, { withCredentials: true });
      if (r.data.insight) setInsight(r.data.insight);
      else toast.info("No medical or support context detected in this note.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI insight failed");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/doorbell/rings/${ringId}/notes`,
        { text: draft.trim(), ai_insight: insight },
        { withCredentials: true },
      );
      setDraft("");
      setInsight(null);
      await load();
      toast.success("Note saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save note");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  // Portal to <body>: the floating Scan2Call widget positions itself with a
  // CSS transform, which would re-anchor our fixed overlay inside it.
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center" data-testid="call-notes-dialog">
      <div className="bg-white text-gray-900 w-full sm:max-w-lg sm:rounded-sm max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <p className="font-heading font-bold tracking-tight flex items-center gap-2 text-sm">
            <NotebookPen className="w-4 h-4 text-emerald-600" />
            Call notes{visitorName ? ` — ${visitorName}` : ""}
          </p>
          {notes.length > 0 && (
            <button
              onClick={() => setEmailOpen((v) => !v)}
              className="ml-auto mr-1 p-1.5 rounded-sm hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
              title="Email PDF to patient/customer"
              aria-label="Email notes PDF"
              data-testid="call-notes-email-btn"
            >
              <Mail className="w-4 h-4" />
            </button>
          )}
          {notes.length > 0 && (
            <button
              onClick={downloadPdf}
              disabled={pdfBusy}
              className="mr-1 p-1.5 rounded-sm hover:bg-gray-100 text-gray-500 hover:text-gray-900 disabled:opacity-50 transition-colors"
              title="Download PDF"
              aria-label="Download notes as PDF"
              data-testid="call-notes-pdf-btn"
            >
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="call-notes-close" aria-label="Close notes">
            <X className="w-4 h-4" />
          </button>
        </div>

        {emailOpen && (
          <form onSubmit={sendEmail} className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex gap-2" data-testid="call-notes-email-row">
            <Input
              type="email"
              autoFocus
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="patient@example.com"
              className="rounded-sm h-9 text-sm bg-white"
              data-testid="call-notes-email-input"
            />
            <Button
              type="submit"
              disabled={emailBusy || !emailTo.trim()}
              className="bg-black hover:bg-gray-800 text-white rounded-sm h-9 font-body font-semibold text-xs shrink-0"
              data-testid="call-notes-email-send"
            >
              {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Send PDF"}
            </Button>
          </form>
        )}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[120px]">
          {notes.length === 0 ? (
            <p className="text-xs text-gray-400 italic font-body">No notes yet for this call.</p>
          ) : notes.map((n) => (
            <div key={n.note_id} className="border border-gray-200 rounded-sm p-2.5" data-testid={`call-note-${n.note_id}`}>
              <p className="text-xs font-body text-gray-800 whitespace-pre-wrap">{n.text}</p>
              {n.ai_insight && (
                <div className="mt-2 bg-violet-50 border border-violet-200 rounded-sm p-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-violet-700 flex items-center gap-1 mb-0.5">
                    <Sparkles className="w-3 h-3" /> AI insight
                  </p>
                  <p className="text-[11px] font-body text-violet-900 whitespace-pre-wrap">{n.ai_insight}</p>
                </div>
              )}
              <p className="text-[10px] text-gray-400 font-body mt-1.5">
                {n.author_name} · {new Date(n.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-200 p-3 space-y-2">
          {insight && (
            <div className="bg-violet-50 border border-violet-200 rounded-sm p-2" data-testid="call-notes-ai-preview">
              <p className="text-[10px] uppercase tracking-widest font-bold text-violet-700 flex items-center gap-1 mb-0.5">
                <Sparkles className="w-3 h-3" /> AI insight (saved with note)
              </p>
              <p className="text-[11px] font-body text-violet-900 whitespace-pre-wrap">{insight}</p>
            </div>
          )}
          <Textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); if (insight) setInsight(null); }}
            placeholder="Symptoms, test results, prescriptions… or a support transcript"
            rows={3}
            maxLength={4000}
            className="rounded-sm text-sm"
            data-testid="call-notes-input"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={askAi}
              disabled={aiBusy || draft.trim().length < 10}
              className="rounded-sm font-body text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="call-notes-ai-btn"
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> AI insight</>}
            </Button>
            <Button
              onClick={save}
              disabled={busy || !draft.trim()}
              className="flex-1 bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs"
              data-testid="call-notes-save-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save note"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
